import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { isRtl } from '@sohanscript/i18n/locale-meta';
import { AstronautRocketLottie } from '@sohanscript/ui/animated/astronaut-grabbing-rocket-lottie';
import { PathTraveler } from '@sohanscript/ui/animated/path-traveller';
import { Heading } from '@sohanscript/ui/heading';
import { Safari } from '@sohanscript/ui/mock/safari';
import { Motion } from '@sohanscript/ui/motion';
import {
	AnimatePresence,
	domMax,
	LazyMotion,
	useMotionValueEvent,
	useScroll,
} from 'motion/react';
import { useRef, useState } from 'react';
import { getLocalizedExperiences } from '#/lib/hooks/sohanemon.tsx';
import { ExperienceDetailCard } from './exp-details-card';
import { MobileExperienceCard } from './exp-details-card-mobile';
import { ExperienceTitle } from './exp-title';

type ExpSectionType = React.ComponentProps<'section'> & { mini?: boolean };

// Tangent-continuous (C then S), short tails so the rocket just clears the edges.
const ROCKET_PATH =
	'M180 -120 C175 0, 185 220, 260 330 S420 440, 560 462 S900 470, 1000 560 S1070 760, 1050 840';

export function ExperienceSection({
	mini,
	className,
	...props
}: ExpSectionType) {
	const containerRef = useRef(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ['start start', 'end end'],
	});

	const [activeIndex, setActiveIndex] = useState(0);
	const experiences = getLocalizedExperiences();

	useMotionValueEvent(scrollYProgress, 'change', (latest) => {
		const next = Math.min(
			Math.floor(latest * experiences.length),
			experiences.length - 1,
		);
		setActiveIndex(next);
	});

	return (
		<LazyMotion features={domMax}>
			<section
				ref={containerRef}
				aria-labelledby="experience-heading"
				className={cn(
					'relative overflow-x-clip',
					!mini && 'lg:h-[460vh]',
					className,
				)}
				{...props}
			>
				<div
					className={cn(
						'container flex flex-col',
						!mini && 'auto-stick-to-nav lg:h-except-navbar',
					)}
				>
					<Heading
						titlePrefix={m.heading_exp_prefix()}
						titleHighlight={m.heading_exp_highlight()}
						description={m.experience_heading_tagline()}
						className="col-span-full h-fit"
					/>
					{!mini && (
						<div className="relative hidden w-full flex-1 grid-cols-2 items-center py-4 lg:grid">
							<div className="relative flex items-center">
								<AnimatePresence initial={false}>
									<ExperienceTitle
										key={activeIndex}
										item={experiences[activeIndex]}
										index={activeIndex}
									/>
								</AnimatePresence>
							</div>

							<Safari className="relative aspect-auto! h-fit">
								<div className="overflow-hidden">
									<Motion
										as="div"
										className="h-px rounded-r-lg bg-linear-90 from-accent via-accent to-primary"
										style={{ scaleX: scrollYProgress, transformOrigin: 'left' }}
									/>
									<div
										className="flex transition-transform duration-500 ease-out"
										style={{
											transform: `translateX(${isRtl() ? '' : '-'}${activeIndex * 100}%)`,
										}}
									>
										{experiences.map((item) => (
											<div
												key={item.company}
												className="min-h-0 flex-[0_0_100%]"
											>
												<ExperienceDetailCard item={item} />
											</div>
										))}
									</div>
								</div>
							</Safari>

							<PathTraveler
								path={ROCKET_PATH}
								viewBox={[1200, 700]}
								progress={scrollYProgress}
								reverse
								rotationOffset={120}
								itemClassName="w-40 xl:w-60"
								drag
							>
								<AstronautRocketLottie className="w-full" />
							</PathTraveler>
						</div>
					)}

					<div
						className={cn(
							'grid-auto-fit-80 relative grid gap-6 py-5',
							!mini && 'py-20 max-lg:pb-0 lg:hidden lg:px-5',
						)}
					>
						{experiences.map((item, index) => (
							<MobileExperienceCard
								key={item.company}
								item={item}
								index={index}
							/>
						))}
					</div>
				</div>
			</section>
		</LazyMotion>
	);
}
